import cx from 'classnames'
import AddNoteToResultModal from 'components/Dashboard/AddNoteToResultModal'
import AmendResultKitIdButton from 'components/Dashboard/AmendKitIdButton'
import ArchiveResultButton from 'components/Dashboard/ArchiveResultButton'
import ConfirmMatchButton from 'components/Dashboard/ConfirmMatchButton'
import IgnoreMatchButton from 'components/Dashboard/IgnoreMatchButton'
import SendRegReminderEmailButton from 'components/Dashboard/SendRegReminderEmailButton'
import { Modal2 } from 'components/Modal'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { formatDate, toFriendlyKitId } from 'helpers/string'
import { useState } from 'react'
import { UnmatchedResults_unmatchedResultsConnection_edges } from './../__generated__/UnmatchedResults'
import KitLink from './KitLink'

const RawHL7 = ({ rawHL7, onClose }) => {
  return (
    <Modal2 isOpen onRequestClose={() => onClose()} header="Raw Result Data">
      <pre>{rawHL7}</pre>
    </Modal2>
  )
}

const RegVsLab = ({ a = '', b = '', className }: { a: string; b: string; className?: string }) => {
  const upperA = a?.trim()?.toUpperCase() || ''
  const upperB = b?.trim()?.toUpperCase() || ''

  if (upperA === upperB) {
    return <p className={cx(className, 'has-text-grey is-family-monospace')}>{upperA}</p>
  }

  return (
    <p className={cx(className, 'is-family-monospace')}>
      {upperA || <span className="has-text-warning">[empty]</span>}
      <br />
      {upperB || <span className="has-text-warning">[empty]</span>}
    </p>
  )
}

const RegInfoMismatchPanel = ({ edge }: { edge: UnmatchedResults_unmatchedResultsConnection_edges }) => {
  return (
    <div className="is-flex is-align-items-center">
      <p className="mr-2 has-text-weight-bold">
        Lab:
        <br />
        Reg:
      </p>
      <RegVsLab className="mr-3" a={edge.node.labEvent.firstName} b={edge.node.statedKit.registrationInfo?.firstName} />
      <RegVsLab className="mr-3" a={edge.node.labEvent.lastName} b={edge.node.statedKit.registrationInfo?.lastName} />
      <RegVsLab a={edge.node.labEvent.dob} b={edge.node.statedKit.registrationInfo?.dob} />
    </div>
  )
}

const ResultHistory = ({ edge }: { edge: UnmatchedResults_unmatchedResultsConnection_edges }) => {
  const result = edge.node
  const [showAddModal, setShowAddModal] = useState(false)
  const [expandHistory, setExpandHistory] = useState(false)

  const history = expandHistory ? result.history : result.history.slice(0, 1)

  return (
    <>
      <div>
        <table className="table is-size-7">
          <tbody>
            {history.map((h, i) => (
              <tr key={i}>
                <td>
                  {formatDate(h.createdAt)}
                  <br />
                  {formatDistanceToNow(parseISO(h.createdAt), {
                    addSuffix: true,
                  })}
                </td>
                <td>{h.adminUserEmail}</td>

                <td>
                  {h.__typename === 'ResultHistoryNoteAddedItem' && <>Note: &ldquo;{h.note}&rdquo;</>}
                  {h.__typename === 'ResultHistoryStatedIdChangedItem' && `Kit ID changed: ${h.newId}`}
                  {h.__typename === 'ResultHistoryReminderEmailSentItem' && 'Reminder email sent'}
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr>
              <td className="has-border-bottom-none has-border-left-none has-border-right-none">
                <button
                  className="button is-text is-small"
                  onClick={(e) => {
                    e.preventDefault()
                    setShowAddModal(true)
                  }}
                >
                  Add note
                </button>
              </td>

              <td className="has-border-bottom-none has-border-left-none has-border-right-none" />

              {!expandHistory && result.history.length > 0 && (
                <td className="has-border-bottom-none has-border-left-none has-border-right-none">
                  <button
                    className="button is-text is-small"
                    onClick={(e) => {
                      e.preventDefault()
                      setExpandHistory(true)
                    }}
                  >
                    Show all {result.history.length} items
                  </button>
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>

      {showAddModal && (
        <Modal2 isOpen onRequestClose={() => setShowAddModal(false)} header="Add note">
          <AddNoteToResultModal onRequestClose={() => setShowAddModal(false)} resultId={result.id} />
        </Modal2>
      )}
    </>
  )
}

export interface ResultRowProps {
  edge: UnmatchedResults_unmatchedResultsConnection_edges
}

const ResultRow = ({ edge }: ResultRowProps) => {
  const result = edge.node
  const [showRaw, setShowRaw] = useState(false)

  const canConfirmStatedKit = edge.node.statedKit?.id && edge.node.statedKit.registrationInfo

  return (
    <>
      <tr
        className={cx({
          'has-background-danger-light': edge.node.labEvent.result === 'POSITIVE',
        })}
      >
        <td>
          <div>
            <p className="is-flex is-align-items-center">
              <strong
                className={cx('mr-1', {
                  'has-text-danger': edge.node.labEvent.eventType === 'EXCEPTION',
                })}
              >
                {edge.node.labEvent.eventType}
              </strong>
              ({edge.node.labEvent.labId}):
              <span className="is-size-7 has-text-grey ml-3">
                {edge.node.labEvent.ingestDetails.filename}
                {edge.node.labEvent.ingestDetails.csvRow !== null && <>:{edge.node.labEvent.ingestDetails.csvRow}</>}
              </span>
            </p>

            <div className="mt-2">
              {edge.node.labEvent.firstName && (
                <span className="mr-3 is-family-monospace">{edge.node.labEvent.firstName}</span>
              )}
              {edge.node.labEvent.lastName && (
                <span className="mr-3 is-family-monospace">{edge.node.labEvent.lastName}</span>
              )}
              {edge.node.labEvent.dob && <span className="is-family-monospace mr-3">{edge.node.labEvent.dob}</span>}

              {edge.node.labEvent.rawData && (
                <button className="button is-small is-text" onClick={() => setShowRaw(true)}>
                  Raw Result Data
                </button>
              )}
            </div>

            {showRaw && <RawHL7 rawHL7={edge.node.labEvent.rawData} onClose={() => setShowRaw(null)} />}

            <div>
              <p className="mt-2">
                {edge.node.statedKit ? (
                  <>
                    Stated kit: <KitLink kit={edge.node.statedKit} />
                  </>
                ) : (
                  <>No kit ID detected</>
                )}

                <AmendResultKitIdButton
                  className="ml-3"
                  resultId={result.id}
                  initialKitId={edge.node.statedKit ? toFriendlyKitId(edge.node.statedKit.id) : ''}
                >
                  Amend stated kit ID
                </AmendResultKitIdButton>
              </p>
            </div>
          </div>

          {result.status === 'IGNORED' && <p>Ignored, will clear on refresh</p>}

          <ResultHistory edge={edge} />
        </td>
        <td>
          <p className="mb-2">{edge.unmatchDetail}</p>

          {edge.node.status === 'REG_INFO_MISMATCH' && <RegInfoMismatchPanel edge={edge} />}

          {edge.node.labEvent.eventType === 'EXCEPTION' && (
            <p className="help is-danger">
              {edge.node.labEvent.exceptionMessage} {edge.node.labEvent.comment}
            </p>
          )}

          {edge.contact && (
            <>
              <p className="mt-2 is-family-monospace">
                {edge.node.statedKit.registrationInfo?.firstName || ''}{' '}
                {edge.node.statedKit.registrationInfo?.lastName || ''} {edge.node.statedKit.registrationInfo?.dob || ''}
              </p>

              <p className="mt-2">
                {edge.contact.emailType} email: {edge.contact.email}
              </p>
            </>
          )}
        </td>
        <td>
          {canConfirmStatedKit && (
            <p>
              <ConfirmMatchButton resultId={result.id} kitId={edge.node.statedKit.id}>
                Confirm Match {edge.node.statedKit.id}
              </ConfirmMatchButton>
            </p>
          )}

          <p>
            <ConfirmMatchButton resultId={result.id} promptForKitId>
              Enter Kit ID
            </ConfirmMatchButton>
          </p>

          <p>
            <IgnoreMatchButton resultId={result.id}>Ignore Result</IgnoreMatchButton>
          </p>

          {result.status === 'UNMATCHED' && (
            <p>
              <button disabled className="button is-small is-info is-outlined is-fullwidth">
                Retry Auto Match
              </button>
            </p>
          )}

          {edge.contact && (
            <p>
              <SendRegReminderEmailButton
                resultId={result.id}
                className="button is-small is-info is-outlined is-fullwidth"
              >
                Send reminder email to {edge.contact.emailType}
              </SendRegReminderEmailButton>
            </p>
          )}

          {result.queueName === 'ResultQueue' && (
            <ArchiveResultButton resultId={result.id} className="button is-small is-info is-outline">
              Archive result
            </ArchiveResultButton>
          )}
        </td>
      </tr>
    </>
  )
}

export default ResultRow
